import { passwordStrength } from 'check-password-strength';
import { passwordInputTests } from './password-input-tests';
import lightbox from './glightbox';

/*global TYPO3*/

(function () {
  'use strict';

  // Create global objects
  window.gs1 = window.gs1 || {};
  TYPO3.lang = TYPO3.lang || {};
  TYPO3.settings = TYPO3.settings || {};

  const exports = {

    initializeUserManagement: function () {
      const checkbox = document.getElementById('deleteProfileConfirmation');
      const submitButton = document.getElementById('submitConfirmDeleteProfile');

      if (checkbox) {
        checkbox.addEventListener('change', function () {
          submitButton.disabled = !this.checked;
        });
      }
    },

    /**
     * Initializes the edit profile functionality by setting up event listeners
     * on input fields to show a notification when *all* fields are modified.
     * The notification is displayed using a lightbox component.
     *
     * The function stores the original values of the first name, last name,
     * and username fields. It then sets up event handlers for blur and input
     * events on these fields to check if all fields have been changed.
     * When all fields are modified, the lightbox is opened to display a hint.
     * Otherwise, it closes the lightbox.
     *
     * The notification is based on the content of an element with ID 'info_full_account_update'
     * defined in the Flexform of the Edit/Update/Delete plugin.
     *
     * @returns {void}
     */
    initializeEditProfile: function () {

      const originalValues = {
        firstName: document.getElementById('firstName')?.value || '',
        lastName: document.getElementById('lastName')?.value || '',
        username: document.getElementById('username')?.value || ''
      };

      const fullAccountUpdateHintHtml = document.getElementById('info_full_account_update')?.innerHTML || '';
      lightbox.setElements([
        {
          'content': fullAccountUpdateHintHtml
        }
      ]);

      /**
       * Checks if all user fields have been modified compared to their original values.
       * @return {boolean} True if all fields (firstName, lastName, username) have changed from their original values, false otherwise.
       */
      function checkAllFieldsChanged() {
        const firstName = document.getElementById('firstName')?.value || '';
        const lastName = document.getElementById('lastName')?.value || '';
        const username = document.getElementById('username')?.value || '';

        return (
          firstName !== originalValues.firstName &&
          lastName !== originalValues.lastName &&
          username !== originalValues.username
        );
      }

      /**
       * Displays a hint based on whether all fields have been changed
       * closes the modal (don't show) if change reverted
       * @return {void} This function does not return a value
       */
      function showHint() {
        if (checkAllFieldsChanged()) {
          lightbox.open();
        } else {
          lightbox.close();
        }
      }

      // Add Event-Handler for specific inputs only
      const inputs = [
        document.getElementById('firstName'),
        document.getElementById('lastName'),
        document.getElementById('username')
      ];
      inputs.forEach(input => {
        // The query is triggered on input, which should not be a performance issue with three
        // fields in the scope, but ensures that the form is not submitted with return without seeing the notice.
        if (input) {
          input.addEventListener('input', function () {
            showHint();
          });
        }
      });
    },

    /**
     * Initializes the password strength checking functionality for a password input field.
     * Sets up event listeners to monitor password input and update strength indicators.
     * Validates the password strength against defined criteria and enables/disables
     * the submit button based on the strength level.
     *
     * The function configures a strength meter with visual feedback and enforces
     * a minimum strength level (3 = 'Strong' / 'Gut') for password submission.
     *
     * The strength levels are defined with specific diversity and length requirements.
     * Language strings are retrieved from TYPO3 localization if available.
     *
     * The update function is triggered on input and blur events of the password field.
     * On form submission, the strength is validated and prevents submission if
     * the minimum level is not met.
     *
     * The strength indicator updates dynamically with text and animation on level changes.
     *
     * @returns {void}
     */
    initializePasswordStrengthCheck: function () {

      const MIN_LEVEL = 3; // 3 = 'Strong' / 'Gut'

      const currentPassword = document.getElementById('currentPassword');
      const newPassword = document.getElementById('newPassword');
      const newPasswordConfirmation = document.getElementById('newPasswordConfirmation');
      const bar = document.getElementById('pwStrength');
      const text = document.getElementById('pwStrengthText');
      const error = document.getElementById('pwError');
      const submitBtn = document.getElementById('submitChangePassword');
      const t = (key, fallback = '') => (window.TYPO3 && TYPO3.lang && TYPO3.lang[key]) || fallback;

      const customOptions = [
        { id: 0, value: t('js.form.passwordStrength.0', 'Schlecht'), minDiversity: 0, minLength: 0 },
        { id: 1, value: t('js.form.passwordStrength.1', 'Sehr schwach'), minDiversity: 2, minLength: 8 },
        { id: 2, value: t('js.form.passwordStrength.2', 'Schwach'), minDiversity: 3, minLength: 10 },
        { id: 3, value: t('js.form.passwordStrength.3', 'Gut'), minDiversity: 4, minLength: 12 },
      ];

      if (newPassword && bar && text && error) {
        const labelsDe = [t('js.form.passwordStrength.0', 'Schlecht'), t('js.form.passwordStrength.1', 'Sehr schwach'), t('js.form.passwordStrength.2', 'Schwach'), t('js.form.passwordStrength.3', 'Gut')];

        let lastLevel = -1;

        const setSubmitEnabled = (enabled) => {
          if (submitBtn) {submitBtn.disabled = !enabled;}
        };

        const animateText = () => {
          text.classList.add('is-animating');
          window.setTimeout(() => text.classList.remove('is-animating'), 180);
        };

        const clearError = () => {
          error.textContent = '';
          // error.classList.remove('shake');
        };

        const update = () => {
          const value = newPassword.value ?? '';

          if (!value) {
            bar.setAttribute('data-strength', '-1');
            text.textContent = '';
            clearError();
            setSubmitEnabled(false);
            lastLevel = -1;
            return;
          }

          const result = passwordStrength(value, customOptions);
          const level = Math.max(0, Math.min(3, result.id));

          bar.setAttribute('data-strength', String(level));

          // Text + soft animation only on step change (not on every keypress)
          if (level !== lastLevel) {
            text.textContent = t('js.form.passwordStrength.label', 'Passwortstärke') + `: ${labelsDe[level]}`;
            animateText();
            lastLevel = level;
          } else if (!text.textContent) {
            // If empty initially
            text.textContent = t('js.form.passwordStrength.label', 'Passwortstärke') + `: ${labelsDe[level]}`;
          }

          // Force minimum level
          if (level < MIN_LEVEL) {
            setSubmitEnabled(false);
            //showError('Bitte ein starkes Passwort wählen (Stufe „Gut“ erforderlich).');
          } else {
            // Password and confirmation match and current password is not empty
            if (newPassword.value === newPasswordConfirmation.value && currentPassword.length > 8) {
              setSubmitEnabled(true);
            }
            clearError();
          }
        };

        const form = newPassword.closest('form');
        if (form) {
          form.addEventListener('submit', (e) => {
            const value = newPassword.value ?? '';
            const level = value ? Math.max(0, Math.min(3, passwordStrength(value).id)) : -1;

            if (level < MIN_LEVEL) {
              e.preventDefault();
              //showError('Bitte ein starkes Passwort wählen (Stufe „Gut“ erforderlich).');
              newPassword.focus();
            }
          });
        }

        newPassword.addEventListener('input', update);
        newPassword.addEventListener('blur', update);

        setSubmitEnabled(false);
        update();
      }
    },

    /**
     * Initializes password visibility toggles for input elements.
     *
     * This function sets up event listeners on elements with the 'data-toggle-element' attribute.
     * When clicked, these elements will toggle the visibility of a password input field
     * by changing its type between 'password' and 'text'. The toggle icon will also change
     * to reflect the current state (show/hide password).
     *
     * The function uses a translation helper to provide localized labels for the toggle icons.
     * It specifically targets input elements and only toggles their type if they are of type 'INPUT'.
     *
     * The function expects the following:
     * - Elements with 'data-toggle-element' attribute that point to an input element by ID
     * - Input elements that are of type 'INPUT'
     * - The TYPO3 global object with a lang property for translation support
     *
     * When the toggle is clicked:
     * - If the target input is of type 'password', it will be changed to 'text' and
     *   the toggle icon will show a "hide password" symbol
     * - If the target input is of type 'text', it will be changed to 'password' and
     *   the toggle icon will show a "show password" symbol
     *
     * The function does not return any value.
     */
    initializeConcealToggles: function()
    {
      const t = (key, fallback = '') => (window.TYPO3 && TYPO3.lang && TYPO3.lang[key]) || fallback;

      const toggleTriggerElements = document.querySelectorAll('[data-toggle-element]');

      toggleTriggerElements.forEach(element => {
        element.addEventListener('click', function () {
          // Extract the ID of the target element from the data attribute
          const targetId = element.getAttribute('data-toggle-element');
          // Find target-element
          const targetElement = document.getElementById(targetId);
          // Check if the target element exists and is of type input
          if (targetElement && targetElement.tagName === 'INPUT') {
            // Check current type
            if (targetElement.type === 'password') {
              // Switch to text (show password)
              targetElement.type = 'text';
              element.innerHTML = '<i class="bi bi-eye-slash-fill" title="' + t('js.form.hidePassword.label', 'Passwort verbergen') + '"></i>';
            } else {
              // Switch to password (hide password)
              targetElement.type = 'password';
              element.innerHTML = '<i class="bi bi-eye-fill" title="' + t('js.form.showPassword.label', 'Passwort anzeigen') + '"></i>️';
            }
          }
        });
      });
    },

    /**
     * Initializes password input validation tests by calling the password input tests function.
     * This method sets up the testing environment for password input fields.
     */
    initializePasswordTests: function()
    {
      passwordInputTests();
    },

    /**
     * Initializes the password confirmation functionality by setting up event listeners
     * to validate that the new password and its confirmation match, and that both
     * passwords meet minimum length requirements.
     *
     * The function attaches input event listeners to the new password and confirmation
     * fields. When either field changes, it checks if the passwords match and if both
     * have a length greater than 8 characters. If the validation passes, the submit
     * button is enabled; otherwise, it is disabled.
     *
     * This function assumes the presence of HTML elements with IDs:
     * 'currentPassword', 'newPassword', 'newPasswordConfirmation', and 'submitChangePassword'.
     */
    initializePasswordConfirmation: function () {

      const currentPasswordInput = document.getElementById('currentPassword');
      const newPasswordInput = document.getElementById('newPassword');
      const newPasswordConfirmationInput = document.getElementById('newPasswordConfirmation');
      const submitButton = document.getElementById('submitChangePassword');

      // Function to check passwords
      function checkPasswords() {
        const currentPassword = currentPasswordInput.value;
        const password = newPasswordInput.value;
        const confirmation = newPasswordConfirmationInput.value;

        // Activate/deactivate the button based on the match
        submitButton.disabled = !(password === confirmation && password.length > 8 && currentPassword.length > 8);
      }

      // Add Event-Listener
      if (newPasswordInput) {
        newPasswordInput.addEventListener('input', checkPasswords);
      }
      if (newPasswordConfirmationInput) {
        newPasswordConfirmationInput.addEventListener('input', checkPasswords);
      }

    }
  };

  exports.initializeUserManagement();
  exports.initializeEditProfile();
  exports.initializePasswordStrengthCheck();
  exports.initializeConcealToggles();
  exports.initializePasswordTests();
  exports.initializePasswordConfirmation();

})();
